import { useCallback, useEffect, useState } from "react";
import ReactFlow, {
  Node,
  Background,
  Position,
  Controls,
  useReactFlow,
  ReactFlowProvider,
  MarkerType,
  BackgroundVariant,
  ConnectionMode,
  NodeTypes,
  EdgeTypes,
} from "reactflow";

import "reactflow/dist/style.css";
import Stage from "../../components/Stage/Stage";
import "./FlowPlayground.scss";
import { INIT_WORKFLOW_STAGE } from "../../models/Workflow";
import { useWorkFlowContext } from "../../context/workflow-context";
import LabelEdge from "../LabelEdge/LabelEdge";
import { useLocation } from "react-router-dom";
import DecisionNode from "../DecisionNode/DecisionNode";
import CreateNode from "../CreateNode/CreateNode";
import EndNode from "../EndNode/EndNode";
import WorkFlowRequestOverviewCard from "../WorkFlowRequestOverviewCard/WorkFlowRequestOverviewCard";

const nodeTypes: NodeTypes = {
  normal: Stage,
  decision: DecisionNode,
  end: EndNode,
  status: WorkFlowRequestOverviewCard,
};

const edgeTypes: EdgeTypes = {
  custom: LabelEdge,
};

const FlowLayout = () => {
  const {
    nodes,
    edges,
    addNodes,
    onConnect,
    onNodesChange,
    onEdgesChange,
    updateNodeDetails,
  } = useWorkFlowContext();
  const [diff, setDiff] = useState({ x: 0, y: 0 });
  const [mode, setMode] = useState("");
  const { screenToFlowPosition } = useReactFlow();
  const getId = () => new Date().getTime();
  const location = useLocation();
  const [openTypes, setOpenTypes] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: -1, y: -1 });

  useEffect(() => {
    if (location.pathname.indexOf("edit") > -1) {
      setMode("edit");
    } else if (location.pathname.indexOf("create") > -1) {
      setMode("create");
    } else {
      setMode("view");
    }
  }, []);

  useEffect(() => {
    if (nodes.length > 0 && startPosition.x === -1 && startPosition.y === -1) {
      let x = nodes[0].position.x;
      let y = nodes[0].position.y;

      nodes.forEach((node) => {
        x = Math.min(x, node.position.x);
        y = Math.min(x, node.position.y);
      });
      setStartPosition({ x: -1 * x + 50, y: -1 * y });
    }
  }, [nodes]);

  const onDrag = (event: React.DragEvent, elm: string) => {
    event.dataTransfer.effectAllowed = "move";
  };

  const onDragStart = (event: React.DragEvent, elm: string) => {
    const pos = document
      .getElementById(`create-elm-${elm}`)
      ?.getBoundingClientRect();
    if (pos) {
      setDiff({
        x: event.clientX - pos.x,
        y: event.clientY - pos.y,
      });
    }
    event.dataTransfer.setData("type", elm);
  };

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const position = screenToFlowPosition({
        x: event.clientX - diff.x,
        y: event.clientY - diff.y,
      });
      const type = event.dataTransfer.getData("type");
      setOpenTypes(false);
      const id = getId();
      const newNode: Node = {
        id: id.toString(),
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        position,
        type,
        data: { id },
      };
      updateNodeDetails({ ...INIT_WORKFLOW_STAGE, id });
      addNodes(newNode);
    },
    [screenToFlowPosition, diff],
  );

  return (
    <div className="full-width full-height position-relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onDrop={onDrop}
        onDragOver={onDragOver}
        connectionMode={ConnectionMode.Loose}
        style={{ background: "#F8F9FA" }}
        // defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        // fitView={mode !== "create"}
        fitViewOptions={{ padding: 100 }}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed, color: "#333333" },
          style: {
            strokeWidth: 2,
            stroke: "#333333",
          },
        }}
        edgesUpdatable={mode !== "view"}
        edgesFocusable={mode !== "view"}
        nodesDraggable={mode !== "view"}
        nodesConnectable={mode !== "view"}
        nodesFocusable={mode !== "view"}
        // elementsSelectable={mode !== "view"}
      >
        <Background variant={BackgroundVariant.Lines} />
        <Controls />
      </ReactFlow>
      {mode !== "view" && (
        <CreateNode
          onDrag={onDrag}
          onDragStart={onDragStart}
          openTypes={openTypes}
          toggleOpenTypes={() => setOpenTypes((val) => !val)}
        />
      )}
    </div>
  );
};

const FlowPlayground = () => {
  return (
    <ReactFlowProvider>
      <FlowLayout />
    </ReactFlowProvider>
  );
};

export default FlowPlayground;
